import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Film, Briefcase, Users, MessageSquareText, MapPin, Handshake } from "lucide-react";

function StatCard({ label, value, hint }) {
  return (
    <div className="ff-card p-6 relative overflow-hidden" data-testid={`home-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="text-xs uppercase tracking-[0.25em] text-neutral-400">{label}</div>
      <div className="mt-3 font-display text-4xl tracking-tighter leading-none">{value}</div>
      <div className="mt-2 text-xs text-neutral-500">{hint}</div>
      <div className="absolute right-4 top-4 opacity-20" />
    </div>
  );
}

function SectionHeader({ icon: Icon, title, action }) {
  return (
    <div className="flex items-end justify-between mb-4 mt-12">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 ff-accent-text" /> Film Front
        </div>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-2">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [members, setMembers] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [f, m, ft, j, p] = await Promise.all([
          api.feed({ limit: 5 }).catch(() => []),
          api.members({ limit: 8 }).catch(() => []),
          api.festivals().catch(() => []),
          api.jobs().catch(() => []),
          api.productions().catch(() => []),
        ]);
        if (!alive) return;
        setFeed(Array.isArray(f) ? f : []);
        setMembers(Array.isArray(m) ? m : []);
        setFestivals(Array.isArray(ft) ? ft : []);
        setJobs(Array.isArray(j) ? j : []);
        setProductions(Array.isArray(p) ? p : []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <div>
      {/* Hero greeting */}
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Home</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium">
          Hey, <span className="ff-accent-text">{firstName}</span>. Ready to roll?
        </h1>
        <p className="text-neutral-400 mt-3 max-w-xl">
          A snapshot of what's happening across the Southern Utah film community today.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 ff-stagger">
        <StatCard label="Members" value={members.length || "—"} hint="Verified crew & pros" />
        <StatCard label="Open Jobs" value={jobs.length} hint="Roles & gigs live" />
        <StatCard label="Festivals" value={festivals.length} hint="Deadlines tracked" />
        <StatCard label="Productions" value={productions.length} hint="On the map" />
      </div>

      {/* Community feed */}
      <SectionHeader
        icon={MessageSquareText}
        title="Community feed"
        action={
          <Link to="/app/community" className="text-sm text-neutral-300 hover:text-white inline-flex items-center gap-1" data-testid="home-see-all-community">
            See all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        }
      />
      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : feed.length === 0 ? (
        <div className="ff-card p-8 text-neutral-400 text-sm" data-testid="home-empty-feed">
          The feed is quiet. Be the first to post a shout-out, casting call, or shoot recap.
          <div className="mt-4">
            <Link to="/app/community" className="ff-btn-primary text-sm">Open community board</Link>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 ff-stagger">
          {feed.slice(0, 4).map((p, i) => (
            <div key={p.id || i} className="ff-card p-6" data-testid={`home-feed-item-${i}`}>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{p.author_name || "Film Front"}</div>
              <div className="text-sm text-neutral-200 line-clamp-3">{p.content || p.body || p.text || "(no content)"}</div>
            </div>
          ))}
        </div>
      )}

      {/* Directory */}
      <SectionHeader
        icon={Users}
        title="New members this week"
        action={
          <Link to="/app/directory" className="text-sm text-neutral-300 hover:text-white inline-flex items-center gap-1">
            Browse directory <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 ff-stagger">
        {(members.slice(0, 4)).map((m, i) => (
          <Link
            key={m.id}
            to={`/app/members/${m.id}`}
            className="ff-card p-5 block group"
            data-testid={`home-member-${i}`}
          >
            <div className="w-14 h-14 rounded-full ff-accent-bg text-black font-semibold flex items-center justify-center mb-3">
              {m.avatar_url
                ? <img src={m.avatar_url} className="w-full h-full rounded-full object-cover" alt="" />
                : (m.name || "?").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()}
            </div>
            <div className="font-display text-base tracking-tight">{m.name}</div>
            <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{m.headline || m.location || "Southern Utah film"}</div>
          </Link>
        ))}
        {members.length === 0 && (
          <div className="col-span-full text-sm text-neutral-500">No members loaded yet.</div>
        )}
      </div>

      {/* Jobs + Festivals two-col */}
      <div className="grid lg:grid-cols-2 gap-6 mt-12">
        <div>
          <SectionHeader icon={Briefcase} title="Latest jobs" action={<Link to="/app/jobs" className="text-sm text-neutral-300 hover:text-white">All jobs →</Link>} />
          <div className="space-y-3">
            {jobs.slice(0,4).map((j, i) => (
              <div key={j.id || i} className="ff-card p-5" data-testid={`home-job-${i}`}>
                <div className="text-xs uppercase tracking-widest text-neutral-500">{j.department || j.type || "Crew"}</div>
                <div className="font-display text-lg tracking-tight mt-1">{j.title || j.role || "Untitled role"}</div>
                <div className="text-xs text-neutral-500 mt-1">{j.location || "Southern Utah"} · {j.rate || j.budget || "Rate TBD"}</div>
              </div>
            ))}
            {jobs.length === 0 && (
              <div className="ff-card p-6 text-sm text-neutral-500">No open jobs. Check back soon.</div>
            )}
          </div>
        </div>
        <div>
          <SectionHeader icon={Film} title="Festivals & deadlines" action={<Link to="/app/festivals" className="text-sm text-neutral-300 hover:text-white">All festivals →</Link>} />
          <div className="space-y-3">
            {festivals.slice(0,4).map((f, i) => (
              <div key={f.id || i} className="ff-card p-5" data-testid={`home-festival-${i}`}>
                <div className="text-xs uppercase tracking-widest text-neutral-500">{f.category || "Festival"}</div>
                <div className="font-display text-lg tracking-tight mt-1">{f.name || f.title || "Festival"}</div>
                <div className="text-xs text-neutral-500 mt-1">{f.location || "Southern Utah"} · {f.deadline || "Deadline TBD"}</div>
              </div>
            ))}
            {festivals.length === 0 && (
              <div className="ff-card p-6 text-sm text-neutral-500">No festivals loaded yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <SectionHeader icon={Handshake} title="Get more from Film Front" />
      <div className="grid md:grid-cols-3 gap-4 ff-stagger">
        <Link to="/app/deal-room" className="ff-card p-6 group" data-testid="home-cta-deal-room">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Deal Room</div>
          <div className="font-display text-xl mt-2">Meet investors & filmmakers</div>
          <div className="text-sm text-neutral-400 mt-2">Networking only — no transactions.</div>
        </Link>
        <Link to="/app/productions" className="ff-card p-6 group" data-testid="home-cta-productions">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Productions</div>
          <div className="font-display text-xl mt-2">See who's shooting</div>
          <div className="text-sm text-neutral-400 mt-2">Live production map for the region.</div>
        </Link>
        <Link to="/app/orgs" className="ff-card p-6 group" data-testid="home-cta-orgs">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Partners</div>
          <div className="font-display text-xl mt-2">Partner organizations</div>
          <div className="text-sm text-neutral-400 mt-2">Studios, film commissions, associations.</div>
        </Link>
      </div>
    </div>
  );
}
